import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";
import { ShieldCheck, Loader2 } from "lucide-react";

const PURPOSES = [
  { id: "core_marketplace", required: true, l: "Operate the marketplace", d: "Create your account, publish listings, and let other users browse them." },
  { id: "chat_storage", required: true, l: "Store your chat messages", d: "Keep chat with counterparties for up to 24 months to help resolve disputes; you can request earlier deletion at any time." },
  { id: "location", required: true, l: "Use your location", d: "Show nearby listings and set your default currency; precision limited to what search needs." },
  { id: "ai_moderation", required: true, l: "AI-assisted moderation", d: "Send your listing photos and text to our AI provider (Gemini via Emergent) for spam / NSFW / stolen-goods checks. Data used for inference only, not for training." },
  { id: "email_updates", required: false, l: "Occasional product updates", d: "Product news and feature announcements. You can turn this off any time." },
];

export default function Consent() {
  const nav = useNavigate();
  const { user, loading, refresh } = useAuth();
  const [ageOk, setAgeOk] = useState(false);
  const [selected, setSelected] = useState(() => new Set(PURPOSES.filter((p) => p.required).map((p) => p.id)));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (loading) return;
    if (!user) return nav("/login");
    // If already consented at current version, skip.
    api.get("/me/consent").then((r) => {
      if (!r.data?.needs_consent) nav("/browse");
    }).catch(() => {});
  }, [user, loading, nav]);

  const toggle = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const submit = async () => {
    if (!ageOk) return toast.error("Please confirm you are 18 or older");
    // required purposes must be selected
    for (const p of PURPOSES) if (p.required && !selected.has(p.id)) return toast.error(`"${p.l}" is required to use PartByPart`);
    setSaving(true);
    try {
      await api.post("/me/consent", { age_confirmed: true, purposes: Array.from(selected) });
      await refresh();
      toast.success("Thanks — you're all set.");
      nav("/browse");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Couldn't save your consent");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !user) return null;

  return (
    <div className="pbp-section py-12 md:py-16 max-w-2xl">
      <div className="text-xs uppercase tracking-[0.18em] text-[#86868b] font-semibold">Welcome to PartByPart</div>
      <h1 className="mt-2 text-3xl md:text-4xl font-semibold tracking-tighter">Your data. Your choice.</h1>
      <p className="mt-4 text-[#1d1d1f]/80 leading-relaxed">
        Under India&rsquo;s Digital Personal Data Protection Act, 2023, we need your informed consent
        for each purpose we process your data. This is a one-time notice &mdash; we won&rsquo;t nag
        you with pop-ups on every action. You can review or withdraw consent any time from
        <a className="pbp-link ml-1" href="/settings">Account settings</a>.
      </p>

      <div className="mt-8 space-y-3" data-testid="consent-purposes">
        {PURPOSES.map((p) => (
          <label key={p.id} className={`block pbp-card p-5 cursor-pointer border-2 ${selected.has(p.id) ? "border-[#0071e3]" : "border-transparent"}`}>
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                data-testid={`consent-${p.id}`}
                disabled={p.required}
                checked={selected.has(p.id)}
                onChange={() => toggle(p.id)}
                className="mt-1 w-5 h-5 accent-[#0071e3]"
              />
              <div className="flex-1">
                <div className="font-semibold flex items-center gap-2">{p.l} {p.required && <span className="pbp-chip bg-[#0071e3]/10 text-[#0071e3] text-[10px]">required</span>}</div>
                <div className="text-sm text-[#86868b] mt-1">{p.d}</div>
              </div>
            </div>
          </label>
        ))}
      </div>

      <label className="mt-6 flex items-start gap-3 cursor-pointer">
        <input type="checkbox" data-testid="consent-age" checked={ageOk} onChange={(e) => setAgeOk(e.target.checked)} className="mt-1 w-5 h-5 accent-[#0071e3]" />
        <span className="text-sm">I confirm I am at least 18 years old. PartByPart is not intended for children under 18.</span>
      </label>

      <p className="mt-6 text-xs text-[#86868b] leading-relaxed">
        Third-party processors used: Emergent (backend), Google (auth &amp; AI), ipapi.co (geolocation),
        object storage for images. We do not sell your data. Grievance Officer:
        <a className="pbp-link mx-1" href="mailto:grievance@partbypart.com">grievance@partbypart.com</a>
        (Mumbai, Maharashtra, India). See <a className="pbp-link" href="/privacy">Privacy Policy</a> for full details.
      </p>

      <div className="mt-8 flex flex-wrap gap-3">
        <button data-testid="consent-agree" disabled={saving} onClick={submit} className="pbp-btn-primary px-6 py-3">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" strokeWidth={1.8} />}
          Agree &amp; continue
        </button>
      </div>
    </div>
  );
}
